/**
 * Switch between the dark and light themes.
 *
 * The mechanism already existed — tokens.css carries both palettes and
 * theme.ts applies the attribute and persists the choice — and nothing
 * exposed it. This is that button.
 *
 * A BUTTON, NOT A THREE-WAY. "Follow the system" is the other common option
 * and it is deliberately absent: the handoff's rule is that product surfaces
 * are dark, so dark is the default rather than a preference to be inferred,
 * and a person who wants light is saying so about this app rather than about
 * their laptop.
 *
 * The icon shows the theme you would GET, not the one you are in — the label
 * says which, because an icon alone is ambiguous in exactly the way that
 * makes people click twice to find out.
 */
import { Moon, Sun } from 'lucide-react'
import { useState } from 'react'

import { setTheme, storedTheme, type Theme } from './theme'

export function ThemeToggle() {
  const [theme, setLocal] = useState<Theme>(() => storedTheme())
  const next: Theme = theme === 'dark' ? 'light' : 'dark'

  return (
    <button
      type="button"
      className="sidebar-foot__icon"
      // Named for what it does, not for what is showing. A screen reader
      // announcing "dark" on a dark page says nothing useful.
      title={`Switch to ${next} mode`}
      aria-label={`Switch to ${next} mode`}
      onClick={() => { setTheme(next); setLocal(next) }}
    >
      {next === 'light' ? <Sun size={16} aria-hidden /> : <Moon size={16} aria-hidden />}
    </button>
  )
}
